import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { isDemo, currentUser } from '../../lib/auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Phone,
  PhoneCall,
  BarChart3,
  Settings,
  Users,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Activity,
  Radio,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

const navItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="w-5 h-5" />,
    path: '/app',
  },
  {
    id: 'voice',
    label: 'Voice Call',
    icon: <PhoneCall className="w-5 h-5" />,
    path: '/app/voice',
  },
  {
    id: 'calls',
    label: 'Calls',
    icon: <Phone className="w-5 h-5" />,
    path: '/app/calls',
    badge: 3,
  },
  {
    id: 'monitor',
    label: 'Live Monitor',
    icon: <Radio className="w-5 h-5" />,
    path: '/app/monitor',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <BarChart3 className="w-5 h-5" />,
    path: '/app/analytics',
  },
  {
    id: 'agents',
    label: 'Agents',
    icon: <Users className="w-5 h-5" />,
    path: '/app/agents',
  },
  {
    id: 'transcripts',
    label: 'Transcripts',
    icon: <MessageSquare className="w-5 h-5" />,
    path: '/app/transcripts',
  },
  {
    id: 'system',
    label: 'System',
    icon: <Activity className="w-5 h-5" />,
    path: '/app/system',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
    path: '/app/settings',
  },
];

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const _u = currentUser();
  const userName = `${_u.charAt(0).toUpperCase()}${_u.slice(1)} user`;
  const userInitial = _u.charAt(0).toUpperCase();

  return (
    <motion.aside
      className={cn(
        'fixed left-0 top-0 h-screen z-40 flex flex-col',
        'backdrop-blur-2xl bg-background/80 border-r border-white/[0.06]',
        className
      )}
      initial={false}
      animate={{ width: collapsed ? 72 : 240 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      {/* Logo area */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-white/[0.06] flex-shrink-0">
        <motion.div
          className="w-9 h-9 rounded-xl bg-vokentra flex items-center justify-center flex-shrink-0"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
              fill="white"
            />
            <circle cx="12" cy="12" r="5" fill="white" />
          </svg>
        </motion.div>

        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <h1 className="text-sm font-bold text-text-primary whitespace-nowrap">
                Vokentra
              </h1>
              <p className="text-[10px] text-text-muted whitespace-nowrap">
                Voice Agentic Ai Platform
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Collapse toggle */}
        <motion.button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            'ml-auto p-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors',
            collapsed && 'ml-0 mx-auto'
          )}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </motion.button>
      </div>

      {/* Navigation (demo account sees a restricted set) */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {(isDemo() ? navItems.filter((n) => ['dashboard', 'voice', 'transcripts'].includes(n.id)) : navItems).map((item, index) => {
          const isActive = location.pathname === item.path;

          return (
            <NavLink
              key={item.id}
              to={item.path}
              className="relative block"
            >
              <motion.div
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors relative',
                  isActive
                    ? 'bg-vokentra/10 text-vokentra'
                    : 'text-text-muted hover:text-text-secondary hover:bg-white/[0.03]'
                )}
                whileHover={!isActive ? { x: 4 } : {}}
                transition={{ duration: 0.2 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                // @ts-expect-error framer-motion types
                transition={{ delay: index * 0.05 }}
              >
                {/* Active indicator */}
                {isActive && (
                  <motion.div
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-vokentra rounded-r-full"
                    layoutId="sidebar-active"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}

                <span className="flex-shrink-0">{item.icon}</span>

                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-sm font-medium whitespace-nowrap overflow-hidden"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Badge */}
                {item.badge !== undefined && item.badge > 0 && !collapsed && (
                  <motion.span
                    className="ml-auto text-[10px] font-bold bg-vokentra text-white px-1.5 py-0.5 rounded-full min-w-[20px] text-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  >
                    {item.badge}
                  </motion.span>
                )}

                {item.badge !== undefined && item.badge > 0 && collapsed && (
                  <motion.span
                    className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-vokentra text-white text-[8px] font-bold rounded-full flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                  >
                    {item.badge}
                  </motion.span>
                )}
              </motion.div>
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom section - status */}
      <div className="p-3 border-t border-white/[0.06]">
        <AnimatePresence>
          {!collapsed ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-3 px-3 py-2"
            >
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-vokentra to-vokentra-dark flex items-center justify-center text-white text-xs font-bold">
                  {userInitial}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-background" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-medium text-text-primary truncate">{userName}</p>
                <p className="text-[10px] text-success">Online</p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-vokentra to-vokentra-dark flex items-center justify-center text-white text-sm font-bold">
                  {userInitial}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-success rounded-full border-2 border-background" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.aside>
  );
}
